import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '../context/authStore';
import api, { API_BASE_URL, authAPI } from '../services/api';
import { useBranding } from '../hooks/useBranding';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

export default function LoginPage() {
  const branding = useBranding();
  const [showPassword, setShowPassword] = useState(false);
  const [ssoProviders, setSsoProviders] = useState([]);
  const [loginError, setLoginError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { login, handleSSOCallback, isLoading, error, clearError } = useAuthStore();
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });
  
  // Handle SSO callback (tokens in URL)
  useEffect(() => {
    const accessToken = searchParams.get('accessToken');
    const refreshToken = searchParams.get('refreshToken');
    
    if (accessToken && refreshToken) {
      handleSSOCallback(accessToken, refreshToken).then((result) => {
        if (result.success) {
          navigate('/dashboard', { replace: true });
        }
      });
    }
    
    // Handle SSO error
    const ssoError = searchParams.get('error');
    if (ssoError === 'sso_failed') {
      // Error is shown via the error state
    }
  }, [searchParams, handleSSOCallback, navigate]);
  
  // Fetch SSO providers
  useEffect(() => {
    authAPI.getProviders().then((response) => {
      setSsoProviders(response.data.providers || []);
    }).catch(() => {
      // Ignore - SSO providers are optional
    });
  }, []);
  
  // Clear error on unmount
  useEffect(() => {
    return () => clearError();
  }, [clearError]);
  
  const onSubmit = async (data) => {
    setLoginError(null);
    const result = await login(data.email, data.password);
    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      setLoginError(result.error);
    }
  };
  
  const handleSSOLogin = (providerId) => {
    // On a custom domain the hostname carries no tenant, so the API host is
    // the one the resolve call answered with — main.jsx adopted it onto the
    // axios instance. The module-level constant is only the guess made
    // before that answer arrived.
    const apiBase = api.defaults.baseURL || API_BASE_URL;
    window.location.href = `${apiBase}/auth/${providerId}`;
  };
  
  const getSSOIcon = (provider) => {
    switch (provider.icon) {
      case 'microsoft':
        return (
          <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
            <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
            <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
          </svg>
        );
      case 'google':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        );
      case 'okta':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#007DC1">
            <path d="M12 0C5.389 0 0 5.35 0 12s5.35 12 12 12 12-5.35 12-12S18.611 0 12 0zm0 18c-3.325 0-6-2.675-6-6s2.675-6 6-6 6 2.675 6 6-2.675 6-6 6z"/>
          </svg>
        );
      default:
        return null;
    }
  };
  
  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-neutral-900">Welcome to Your Client Portal</h2>
        <p className="text-neutral-600 mt-2">
          Don't have an account?{' '}
          <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
            Register here
          </Link>
        </p>
      </div>
      
      {/* Error message */}
      {(loginError || error || searchParams.get('error')) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700 text-sm font-medium">
              {loginError || error || (searchParams.get('error') === 'sso_failed'
                ? 'Single sign-on authentication failed. Please try again or use email login.'
                : 'Authentication failed. Please try again.')}
            </p>
            {((loginError || error)?.includes('verification') || (loginError || error)?.includes('complete your registration')) && (
              <p className="text-red-600 text-xs mt-1">
                Check your email inbox (and spam folder) for the verification link.
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* SSO Providers */}
      {ssoProviders.length > 0 && (
        <>
          <div className="space-y-3 mb-6">
            {ssoProviders.map((provider) => (
              <button
                key={provider.id}
                onClick={() => handleSSOLogin(provider.id)}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 
                         border border-neutral-300 rounded-lg font-medium text-neutral-700
                         hover:bg-neutral-50 transition-colors disabled:opacity-50"
              >
                {getSSOIcon(provider)}
                Continue with {provider.name}
              </button>
            ))}
          </div>
          
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-neutral-500">or continue with email</span>
            </div>
          </div>
        </>
      )}
      
      {/* Email/Password form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label htmlFor="email" className="label">Email address</label>
          <input
            {...register('email')}
            type="email"
            id="email"
            autoComplete="email"
            className={`input ${errors.email ? 'input-error' : ''}`}
            placeholder="you@company.com"
          />
          {errors.email && (
            <p className="error-text">{errors.email.message}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="password" className="label">Password</label>
          <div className="relative">
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              className={`input pr-12 ${errors.password ? 'input-error' : ''}`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.password && (
            <p className="error-text">{errors.password.message}</p>
          )}
        </div>
        
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
            />
            <span className="text-sm text-neutral-600">Remember me</span>
          </label>
          
          <Link 
            to="/forgot-password" 
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Forgot password?
          </Link>
        </div>
        
        <button
          type="submit"
          disabled={isSubmitting || isLoading}
          className="w-full btn-primary py-3"
        >
          {(isSubmitting || isLoading) ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </button>
      </form>
      
      {branding.supportEmail && (
        <p className="mt-8 text-center text-sm text-neutral-500">
          Need help? Contact us at{' '}
          <a href={`mailto:${branding.supportEmail}`} className="text-primary-600 hover:text-primary-700">
            {branding.supportEmail}
          </a>
        </p>
      )}
    </div>
  );
}
